import { useEffect, useRef, useState } from 'react'
import useSWR from 'swr'
import { cdnUrl } from 'commons/cdn'
import { MyImage } from 'components/MyImage'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useToast } from '@/components/ui/use-toast'
import { putFetcher } from 'api/swr'
import { cn } from '@/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'

const BADGES = [
  { id: 'new', label: 'New' },
  { id: 'popular', label: 'Popular' },
  { id: 'recommend', label: 'Recommend' },
  { id: 'hot', label: 'Hot' },
]

const BUTTONS = [
  'Try Now',
  'Get Full Set',
  'Try It On',
  'Tap to Change Outfit',
]

function Thumb({ src, selected, label, onClick, disabled }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'rounded-xl border-2 border-transparent bg-white',
        'hover:border-primary',
        selected && 'border-primary',
        disabled && 'opacity-50',
      )}
      title={label}
    >
      {src ? (
        <img
          alt={label}
          className="h-32 w-32 rounded-xl object-contain"
          src={cdnUrl(src)}
        />
      ) : (
        <div className="flex h-32 w-32 items-center justify-center rounded-xl bg-muted text-xs text-muted-foreground">
          无图
        </div>
      )}
    </button>
  )
}

export function OutfitListConfigDialog({ open, onOpenChange, row, mutate }) {
  const data = row.original
  const { toast } = useToast()
  const [pending, setPending] = useState(false)
  const [outfitIndex, setOutfitIndex] = useState(0)
  const [badges, setBadges] = useState([])
  const [listButton, setListButton] = useState('')
  const pickedCoverRef = useRef(false)

  const { data: coverData, isLoading: coversLoading } = useSWR(
    open && data.id ? [`/template-lists/outfit/covers/${data.id}`] : null,
  )

  const savedOutfitIndex =
    data.coverKind === 'outfit' ? (data.outfitIndex ?? 0) : 0

  useEffect(() => {
    if (!open) {
      pickedCoverRef.current = false
      return
    }
    pickedCoverRef.current = false
    setOutfitIndex(savedOutfitIndex)
    setBadges(data.listBadges ?? [])
    setListButton(data.listButton ?? '')
  }, [open, data.id])

  const covers = coverData?.defaultSetCovers ?? []

  useEffect(() => {
    if (!open || !coverData || pickedCoverRef.current) {
      return
    }
    const list = coverData.defaultSetCovers ?? []
    if (list[savedOutfitIndex]) {
      setOutfitIndex(savedOutfitIndex)
      return
    }
    const first = list.findIndex(Boolean)
    setOutfitIndex(first >= 0 ? first : 0)
  }, [open, data.id, coverData, savedOutfitIndex])

  const save = async () => {
    if (pending) {
      return
    }
    const listCoverUrl = covers[outfitIndex]
    if (!listCoverUrl) {
      toast({
        title: '无法保存',
        description: '请选择一张套装图',
        variant: 'destructive',
      })
      return
    }
    setPending(true)
    try {
      await putFetcher('/template-lists/outfit/item', {
        arg: {
          templateId: data.id,
          coverKind: 'outfit',
          outfitIndex,
          badges,
          listButton,
          listCoverUrl,
        },
      })
      mutate?.()
      toast({
        title: '已保存',
        description: '列表展示配置已更新',
        className: 'bg-green-500 text-white',
      })
      onOpenChange(false)
    } catch (error) {
      toast({
        title: '保存失败',
        description: error.message || 'Unknown Error',
        variant: 'destructive',
      })
    } finally {
      setPending(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>配置列表展示</DialogTitle>
          <DialogDescription>
            选择套装主图、标签和按钮文案。
            {data.inOnlines === false
              ? ' 未上线模板也可先配好，上线后客户端会用这份配置。'
              : ''}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <p className="text-sm font-medium">主图</p>
            {coversLoading ? (
              <p className="text-sm text-muted-foreground">套装图加载中…</p>
            ) : null}
            {!coversLoading && !covers.some(Boolean) ? (
              <p className="text-sm text-muted-foreground">这个模板没有套装图</p>
            ) : null}
            <div className="flex flex-wrap gap-2">
              {[0, 1, 2, 3].map((index) => (
                <Thumb
                  key={index}
                  src={covers[index]}
                  selected={outfitIndex === index && Boolean(covers[index])}
                  label={`套装${index + 1}`}
                  disabled={pending || !covers[index]}
                  onClick={() => {
                    pickedCoverRef.current = true
                    setOutfitIndex(index)
                  }}
                />
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-sm font-medium">标签</p>
            <div className="flex flex-wrap gap-2">
              {BADGES.map((badge) => {
                const active = badges.includes(badge.id)
                return (
                  <Button
                    key={badge.id}
                    type="button"
                    size="sm"
                    variant={active ? 'default' : 'outline'}
                    className="h-8 px-3 text-xs"
                    disabled={pending}
                    onClick={() => {
                      setBadges(active ? [] : [badge.id])
                    }}
                  >
                    {badge.label}
                  </Button>
                )
              })}
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-sm font-medium">按钮文案</p>
            <div className="flex flex-wrap gap-2">
              {BUTTONS.map((label) => {
                const active = listButton === label
                return (
                  <Button
                    key={label}
                    type="button"
                    size="sm"
                    variant={active ? 'default' : 'outline'}
                    className="h-8 px-3 text-xs"
                    disabled={pending}
                    onClick={() => setListButton(active ? '' : label)}
                  >
                    {label}
                  </Button>
                )
              })}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={pending}
          >
            取消
          </Button>
          <Button
            onClick={save}
            disabled={pending || coversLoading || !covers[outfitIndex]}
          >
            {pending ? '保存中…' : '保存'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function OutfitListCoverCell({ row }) {
  const url = row.original.listCoverUrl
  if (!url) {
    return <div className="h-24 w-24 rounded-md border border-dashed" />
  }
  return (
    <MyImage
      alt="list cover"
      className="h-24 w-24 rounded-md border border-border bg-black object-contain"
      src={cdnUrl(url)}
    />
  )
}

export function OutfitListBadgeCell({ row }) {
  const badge = (row.original.listBadges ?? [])[0]
  if (!badge) {
    return <span className="text-muted-foreground">—</span>
  }
  return (
    <Badge variant="secondary" className="text-[10px]">
      {badge}
    </Badge>
  )
}

export function OutfitListButtonCell({ row }) {
  const listButton = row.original.listButton
  if (!listButton) {
    return <span className="text-muted-foreground">—</span>
  }
  return <span className="text-xs">{listButton}</span>
}
